/**
 * StudentBookingsTab — embedded inside StudentDetail via render-prop injection.
 *
 * Lists bookings filtered to this student — trials, make-ups, ad-hoc,
 * and regular session bookings. Read-only view; full management is on
 * the top-level /bookings page.
 */

import React from 'react';
import styled from 'styled-components';

import {
  useBookings,
  type BookingResponse,
} from '@features/bookings/api/bookings';
import {
  statusColor,
  statusLabel,
  typeLabel,
} from '@features/bookings/config/ui';

// ---------------------------------------------------------------------------
// Styled helpers (mirrors BookingsPage style for consistency)
// ---------------------------------------------------------------------------

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const StatusCell = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

interface Props {
  studentId: string;
}

export const StudentBookingsTab: React.FC<Props> = ({ studentId }) => {
  const { data, isLoading, isError } = useBookings({ studentId, limit: 100 });

  if (isLoading) {
    return <EmptyState>Loading bookings…</EmptyState>;
  }
  if (isError) {
    return <EmptyState style={{ color: '#dc2626' }}>Failed to load bookings.</EmptyState>;
  }

  const items: BookingResponse[] = data?.items ?? [];

  if (items.length === 0) {
    return (
      <EmptyState data-testid={`candeza-student-bookings-empty-${studentId}`}>
        No bookings found for this student.
      </EmptyState>
    );
  }

  return (
    <Table data-testid={`candeza-student-bookings-table-${studentId}`}>
      <thead>
        <tr>
          <Th>Type</Th>
          <Th>Subject</Th>
          <Th>Starts at</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((bk) => (
          <tr
            key={bk.id}
            data-testid={`candeza-student-booking-row-${bk.id}`}
            data-status={bk.status}
          >
            <Td>{typeLabel(bk.booking_type)}</Td>
            <Td>{bk.subject_id}</Td>
            <Td>{formatDt(bk.starts_at)}</Td>
            <Td>
              <StatusCell>
                <StatusDot $color={statusColor(bk.status)} />
                {statusLabel(bk.status)}
              </StatusCell>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

